import { createSlice } from '@reduxjs/toolkit'
import {
  AllFreeWidthColumn,
  IsAddRule,
  IsAddSubRule,
  IsLoadProfileOptionsAction,
  IsSetCurrentRuleSelected,
} from '../../Constants/Configuration'
import {
  ArticleColumn,
  ArticleDefaultColumn,
  defaultEmptySubRuleTableColumns,
  defaultSubRuleTableColumns,
  SubRuleColumns,
} from '../../Constants/TableConfiguration'
import { mapWorkspaceTableColFilters } from '../../Helpers/ConfigurationHelper'
import { updateEmptyColumnTableFiltersValue } from '../../Helpers/FilterHelper'
import { isEmptyObject } from '../../Helpers/GenericHelpers'
import { ITag } from 'Services/Kiosk/TagsService'

export const subRuleSlice = createSlice({
  name: 'subRule',
  initialState: {
    subRuleId: undefined, //<0 virtual, >0 real
    defaultColumns: SubRuleColumns as ArticleDefaultColumn[],
    filterColumns: defaultSubRuleTableColumns,
    emptyFilterColumns: defaultEmptySubRuleTableColumns,
    filterResultByAvailability: false,
    negateColumns: [],
    tableFilters: {},
    tableFiltersTags: [],
    subRuleArticles: { results: [], totalCount: 0 },
    sorting: {
      sortField: '',
      sortOrder: '',
      pageSize: 20,
      current: 1,
    },
    count: 0,
    activeAutocomplete: '',
    activeDropdown: '',
    autocompleteValues: {},
    freeWidthColumns: [],
    newLineColumns: [],
    listingsDiscountToSave: [],
    filterDiscountVisibilityDropdownValue: 'all',
    editSelection: [],
    loading: false,
    importedSubRule: { ruleId: 0, ruleName: '' },
  },
  reducers: {
    setSubRuleId: (state, action) => {
      if (!action.payload || action.payload !== state.subRuleId) {
        state.listingsDiscountToSave = []
        state.autocompleteValues = {}
      }

      state.subRuleId = !action.payload ? undefined : action.payload
    },
    setSeasonFilter: (state, action) => {
      //set season change only if new id is an already saved rule
      if (action.payload && state.subRuleId && state.subRuleId > 0) {
        state.tableFilters = {
          [ArticleColumn.Season]: action.payload,
        }
      }
    },
    setSubRuleArticles: (state, action) => {
      state.subRuleArticles = action.payload.results
      state.loading = false

      if (state.count !== action.payload.count) {
        state.count = action.payload.count
      }
    },
    setImportedSubRule: (state, action) => {
      state.importedSubRule = action.payload
    },
    saveFilterColumns: (state, action) => {
      state.filterColumns = action.payload

      state.filterColumns.predefinedFilters.forEach(item => {
        if (!item.active && item.value !== ArticleColumn.Season) {
          delete state.tableFilters[item.value]
          delete state.autocompleteValues[item.value]
          if (item.value === ArticleColumn.Tag) {
            state.tableFiltersTags = []
          }
          const elementIndex = state.negateColumns.indexOf(item.value)
          if (elementIndex !== -1) {
            state.negateColumns.splice(elementIndex, 1)
          }
        }

        state.emptyFilterColumns.predefinedFilters.forEach(emptyFilter => {
          if (emptyFilter.value === item.value && emptyFilter.active) {
            const elementIndex = state.negateColumns.indexOf(item.value)
            if (elementIndex !== -1) {
              state.negateColumns.splice(elementIndex, 1)
            }
          }
        })
      })
    },
    setFilterResultByAvailability: state => {
      state.filterResultByAvailability = !state.filterResultByAvailability
    },
    saveSorting: (state, action) => {
      if (action.payload.sortField) {
        state.sorting.sortField = action.payload.sortField
      } else {
        state.sorting.sortField = ''
      }
      if (action.payload.sortOrder) {
        state.sorting.sortOrder = action.payload.sortOrder
      } else {
        state.sorting.sortOrder = ''
      }
      if (action.payload.pageSize) {
        state.sorting.pageSize = action.payload.pageSize
      }
      if (action.payload.current) {
        state.sorting.current = action.payload.current || 1
      }
    },
    setActiveSubRuleAutocomplete: (state, action) => {
      state.activeAutocomplete = action.payload

      if (state.activeDropdown !== '') {
        state.activeDropdown = ''
      }
    },
    resetDynamicSubRuleLabelToInitial: (state, action) => {
      if (state.tableFilters[action.payload.label]) {
        delete state.tableFilters[action.payload.label]
        if (action.payload.label === ArticleColumn.Tag) {
          state.tableFiltersTags = []
        }
      }

      if (action.payload.columnEmpty) {
        const emptyTableColumnKeys = Object.keys(state.emptyFilterColumns)
        emptyTableColumnKeys.forEach(key => {
          for (let element in state.filterColumns[key]) {
            state.emptyFilterColumns[key].forEach(emptyElement => {
              if (emptyElement.value === action.payload.label) {
                if (emptyElement.value === state.filterColumns[key][element].value) {
                  emptyElement.active = false

                  if (state.tableFilters[action.payload.label]) {
                    delete state.tableFilters[action.payload.label]
                  }
                }
              }
            })
          }
        })
      }

      state.activeAutocomplete = ''
    },
    setSubRuleAutocompleteValues: (state, action) => {
      state.autocompleteValues[action.payload.label] = action.payload.text
    },
    saveSubruleFilters: (state, action) => {
      const isMultipleSearchField = SubRuleColumns.find(x => x.name === action.payload.key)?.multipleSearch
      if (isMultipleSearchField) {
        const searchedValues = state.tableFilters[action.payload.key] ?? ''
        const searchedSplitted = searchedValues.split('|')
        if (searchedSplitted.indexOf(action.payload.value) === -1) {
          const divider = searchedValues.length === 0 ? '' : '|'
          state.tableFilters[action.payload.key] = searchedValues.concat(divider, action.payload.value)
        }
      } else {
        state.tableFilters[action.payload.key] = action.payload.value
      }

      if (action.payload.key === ArticleColumn.ProductCode) {
        const productCodeFilter = state.filterColumns.predefinedFilters.find(x => x.value === ArticleColumn.ProductCode)
        if (productCodeFilter) productCodeFilter.active = true
      }
      if (action.payload.key === ArticleColumn.SuperCategory) {
        const superCategoryFilter = state.filterColumns.predefinedFilters.find(
          x => x.value === ArticleColumn.SuperCategory,
        )
        if (superCategoryFilter) superCategoryFilter.active = true
        const categoryFilter = state.filterColumns.predefinedFilters.find(x => x.value === ArticleColumn.Category)
        if (categoryFilter) categoryFilter.active = false
      }
    },
    saveSubruleFilterTags: (state, action) => {
      //action.payload as ITag
      const tagStored = state.tableFiltersTags.find((x: ITag) => {
        return x.id === action.payload?.id
      })

      if (!tagStored) {
        state.tableFiltersTags.push(action.payload)
      }
    },
    removeSubruleFilterTags: (state, action) => {
      const tagStored = state.tableFiltersTags.find((x: ITag) => {
        return x.slug === action.payload
      })

      if (tagStored) {
        state.tableFiltersTags = state.tableFiltersTags.filter(x => x.slug !== action.payload)
      }
    },
    saveNegateColumns: (state, action) => {
      if (state.negateColumns.indexOf(action.payload) === -1) {
        state.negateColumns.push(action.payload)
      }

      state.activeDropdown = ''
    },
    resetNegateColumns: state => {
      state.negateColumns.forEach(element => {
        if (state.tableFilters[element]) {
          delete state.tableFilters[element]
          delete state.autocompleteValues[element]
        }
      })

      if (state.negateColumns.length !== 0) {
        state.negateColumns = []
      }
    },
    removeNegateColumn: (state, action) => {
      const elementIndex = state.negateColumns.indexOf(action.payload)
      if (elementIndex !== -1) {
        state.negateColumns.splice(elementIndex, 1)
      }
    },
    removeSubRuleNegateColumnsAndAssociatedFilters: (state, action) => {
      const elementIndex = state.negateColumns.indexOf(action.payload)
      if (elementIndex !== -1) {
        state.negateColumns.splice(elementIndex, 1)

        if (Object.keys(state.tableFilters).indexOf(action.payload) !== -1) {
          delete state.tableFilters[action.payload]
          delete state.autocompleteValues[action.payload]
        }
      }
    },
    saveFreeWidthColumn: (state, action) => {
      if (state.freeWidthColumns.indexOf(action.payload) === -1) {
        state.freeWidthColumns.push(action.payload)
      }

      state.activeDropdown = ''
    },
    saveAllFreeWidthColumn: state => {
      const elementIndex = state.freeWidthColumns.indexOf(AllFreeWidthColumn)
      if (elementIndex === -1) {
        state.freeWidthColumns.push(AllFreeWidthColumn)
      }

      state.activeDropdown = ''
    },
    resetFreeWidthColumns: state => {
      if (state.freeWidthColumns.length !== 0) {
        state.freeWidthColumns = []
      }

      state.activeDropdown = ''
    },
    saveNewLineColumns: (state, action) => {
      const elementIndex = state.newLineColumns.indexOf(action.payload)
      if (elementIndex === -1) {
        state.newLineColumns.push(action.payload)
      } else {
        state.newLineColumns.splice(elementIndex, 1)
      }

      state.activeDropdown = ''
    },
    setActiveDropdown: (state, action) => {
      state.activeDropdown = state.activeDropdown === action.payload ? '' : action.payload
      if (state.activeAutocomplete !== '') {
        state.activeAutocomplete = ''
      }
    },
    updateSubRuleEmptyColumn: (state, action) => {
      const emptyTableColumnKeys = Object.keys(state.emptyFilterColumns)
      emptyTableColumnKeys.forEach(key => {
        for (let element in state.filterColumns[key]) {
          state.emptyFilterColumns[key].forEach(emptyElement => {
            if (emptyElement.value === action.payload.value) {
              if (emptyElement.value === state.filterColumns[key][element].value) {
                emptyElement.active = action.payload.active

                if (!action.payload.active && state.tableFilters[action.payload.value]) {
                  delete state.tableFilters[action.payload.value]
                  delete state.autocompleteValues[action.payload.value]
                  if (action.payload.value === ArticleColumn.Tag) {
                    state.tableFiltersTags = []
                  }
                }
              }
            }
          })
        }
      })
    },
    setEmptyColumnFromMenu: (state, action) => {
      const elementIndex = state.negateColumns.indexOf(action.payload)
      if (elementIndex !== -1) {
        state.negateColumns.splice(elementIndex, 1)
      }

      if (state.tableFilters[action.payload]) {
        delete state.tableFilters[action.payload]
      }

      state.emptyFilterColumns.predefinedFilters.forEach((item, index) => {
        if (item.value === action.payload) {
          state.emptyFilterColumns.predefinedFilters[index].active = true
        }
      })

      updateEmptyColumnTableFiltersValue(state.emptyFilterColumns, state.tableFilters)

      state.activeDropdown = ''
    },
    removeColumn: (state, action) => {
      state.filterColumns.predefinedFilters.forEach(element => {
        if (element.value === action.payload) {
          element.active = false
          delete state.autocompleteValues[element.value]
          const elementIndex = state.negateColumns.indexOf(element.value)
          if (elementIndex !== -1) {
            state.negateColumns.splice(elementIndex, 1)
          }
        }
      })

      updateEmptyColumnTableFiltersValue(state.emptyFilterColumns, state.tableFilters)
    },
    clearSubRuleTagFilters: (state, action) => {
      if (!isEmptyObject(state.tableFilters)) {
        const keepSeasonObj = { [ArticleColumn.Season]: state.tableFilters[ArticleColumn.Season] }
        state.tableFilters = keepSeasonObj
      }

      if (!isEmptyObject(state.autocompleteValues)) {
        state.autocompleteValues = {}
      }

      if (state.tableFiltersTags.length !== 0) {
        state.tableFiltersTags = []
      }

      state.emptyFilterColumns = defaultEmptySubRuleTableColumns
      if (state.negateColumns.length !== 0) {
        state.negateColumns = []
      }
    },
    removeSubRuleFilter: (state, action) => {
      if (Object.keys(state.tableFilters).indexOf(action.payload) !== -1) {
        delete state.tableFilters[action.payload]
        delete state.autocompleteValues[action.payload]
        if (action.payload === ArticleColumn.Tag) {
          state.tableFiltersTags = []
        }
      }
    },
    setListingsDiscountToSave: (state, action) => {
      const isSaved = state.listingsDiscountToSave.find(
        x => x.article === action.payload.article && x.channel === action.payload.channel,
      )

      if (isSaved) {
        const savedIndex = state.listingsDiscountToSave.findIndex(
          x => x.article === action.payload.article && x.channel === action.payload.channel,
        )

        if (savedIndex !== -1) {
          state.listingsDiscountToSave.splice(savedIndex, 1, action.payload)
        }
      } else {
        state.listingsDiscountToSave.push(action.payload)
      }
    },
    resetListingDiscounts: state => {
      state.listingsDiscountToSave = []
    },
    setEditSelectionValuesToListingDiscountToSave: (state, action) => {
      state.editSelection.forEach(articleId => {
        const newElementToAdd = {
          article: articleId,
          channel: action.payload.channel,
          discount: action.payload.discount,
        }

        const isSaved = state.listingsDiscountToSave.find(
          x => articleId === x.article && x.channel === action.payload.channel,
        )

        if (isSaved) {
          const savedIndex = state.listingsDiscountToSave.findIndex(
            x => articleId === x.article && x.channel === action.payload.channel,
          )

          if (savedIndex !== -1) {
            state.listingsDiscountToSave.splice(savedIndex, 1, newElementToAdd)
          }
        } else {
          state.listingsDiscountToSave.push(newElementToAdd)
        }
      })
    },
    removeListingsDiscountToSave: (state, action) => {
      const isSaved = state.listingsDiscountToSave.find(
        x => x.article === action.payload.article && x.channel === action.payload.channel,
      )

      if (isSaved) {
        const savedIndex = state.listingsDiscountToSave.findIndex(
          x => x.article === action.payload.article && x.channel === action.payload.channel,
        )

        if (savedIndex !== -1) {
          state.listingsDiscountToSave.splice(savedIndex, 1)
        }
      }
    },
    emptyListingsDiscountToSave: state => {
      state.listingsDiscountToSave = []
    },
    setFilterDiscountVisibilityDropdownValue: (state, action) => {
      state.filterDiscountVisibilityDropdownValue = action.payload
    },
    setEditSelection: (state, action) => {
      state.editSelection = action.payload
    },
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    resetEmptyColumns: state => {
      const emptyTableColumnKeys = Object.keys(state.emptyFilterColumns)
      emptyTableColumnKeys.forEach(key => {
        for (let element in state.filterColumns[key]) {
          state.emptyFilterColumns[key].forEach(emptyElement => {
            if (emptyElement.value === state.filterColumns[key][element].value && emptyElement.active) {
              if (state.tableFilters[emptyElement.value]) {
                delete state.tableFilters[emptyElement.value]
              }
            }
          })
        }
      })

      state.emptyFilterColumns = defaultEmptySubRuleTableColumns
    },
    updateMultipleSearchFilters: (state, action) => {
      const searchedValues = state.tableFilters[action.payload.key] ?? ''
      const searchedSplitted = searchedValues.split('|')
      if (searchedSplitted.indexOf(action.payload.value) !== -1) {
        state.tableFilters[action.payload.key] = searchedSplitted.filter(x => x !== action.payload.value).join('|')
      }
    },
  },
  extraReducers: builder => {
    builder
      .addMatcher(IsSetCurrentRuleSelected, (state, action) => {
        state.subRuleId = undefined
        state.tableFilters = {}
        state.tableFiltersTags = []
        state.listingsDiscountToSave = []
        state.autocompleteValues = {}
      })
      .addMatcher(IsAddRule, (state, action) => {
        state.subRuleId = undefined
        state.tableFilters = {}
        state.tableFiltersTags = []
        state.listingsDiscountToSave = []
      })
      .addMatcher(IsAddSubRule, (state, action) => {
        state.subRuleId = action.payload.virtualSubrule.virtualId

        const filterToSave = {}

        if (action.payload.season) {
          filterToSave[ArticleColumn.Season] = action.payload.season
        }

        state.tableFilters = filterToSave
        state.tableFiltersTags = []
      })
      .addMatcher(IsLoadProfileOptionsAction, (state, action) => {
        const tableColumnsToSet = mapWorkspaceTableColFilters(
          action.payload.configurationName,
          action.payload.defaultProfileOptions,
          defaultSubRuleTableColumns,
          action.payload.user,
          action.payload.serverConfig,
        )
        tableColumnsToSet.predefinedFilters.forEach(filter => {
          filter.active =
            filter.value === ArticleColumn.Category ||
            filter.value === ArticleColumn.Season ||
            filter.value === ArticleColumn.Brand ||
            filter.value === ArticleColumn.Department ||
            filter.value === ArticleColumn.ArticleCode
              ? true
              : false
        })
        state.filterColumns = tableColumnsToSet
      })
  },
})

export const {
  setSubRuleId,
  setSubRuleArticles,
  saveFilterColumns,
  setFilterResultByAvailability,
  saveSorting,
  setActiveSubRuleAutocomplete,
  resetDynamicSubRuleLabelToInitial,
  setSubRuleAutocompleteValues,
  saveSubruleFilters,
  saveSubruleFilterTags,
  removeSubruleFilterTags,
  setActiveDropdown,
  removeNegateColumn,
  saveFreeWidthColumn,
  saveNegateColumns,
  saveAllFreeWidthColumn,
  resetFreeWidthColumns,
  saveNewLineColumns,
  updateSubRuleEmptyColumn,
  setEmptyColumnFromMenu,
  removeColumn,
  clearSubRuleTagFilters,
  removeSubRuleFilter,
  removeSubRuleNegateColumnsAndAssociatedFilters,
  setListingsDiscountToSave,
  removeListingsDiscountToSave,
  setFilterDiscountVisibilityDropdownValue,
  emptyListingsDiscountToSave,
  setEditSelectionValuesToListingDiscountToSave,
  setEditSelection,
  setSeasonFilter,
  setLoading,
  resetListingDiscounts,
  resetEmptyColumns,
  resetNegateColumns,
  updateMultipleSearchFilters,
  setImportedSubRule,
} = subRuleSlice.actions

export default subRuleSlice.reducer
